import { useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'state/store';
import { fetchCompanyDetailsAsync, clearCompanyData } from 'state/authentication/authenticationSlice';
import { Logger } from 'utils/Logger';

export const useCompanyData = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { user, company, loading, error, activeCompanyId } = useSelector((state: RootState) => state.authentication);
    const fetchedCompanyIdRef = useRef<string | null>(null);
    const failedCompanyIdRef = useRef<string | null>(null);

    const effectiveCompanyId = activeCompanyId || user?.companyId || null;

    const refetchCompanyData = useCallback(() => {
        if (effectiveCompanyId) {
            Logger.debug('useCompanyData', 'Manually refetching company details');
            failedCompanyIdRef.current = null;
            dispatch(fetchCompanyDetailsAsync(effectiveCompanyId));
            fetchedCompanyIdRef.current = effectiveCompanyId;
        }
    }, [effectiveCompanyId, dispatch]);

    useEffect(() => {
        if (!user || !effectiveCompanyId) return;
        if (loading) return;
        if (failedCompanyIdRef.current === effectiveCompanyId) return;
        if (company && fetchedCompanyIdRef.current === effectiveCompanyId) return;

        Logger.debug('useCompanyData', `Fetching company details for companyId: ${effectiveCompanyId}`);
        dispatch(fetchCompanyDetailsAsync(effectiveCompanyId)).then((result: any) => {
            if (result.meta.requestStatus === 'rejected') {
                failedCompanyIdRef.current = effectiveCompanyId;
            }
        });
        fetchedCompanyIdRef.current = effectiveCompanyId;
    }, [effectiveCompanyId, user, company, loading, dispatch]);

    useEffect(() => {
        if (!user) {
            if (company) {
                Logger.debug('useCompanyData', 'Clearing company data - no user');
                dispatch(clearCompanyData());
            }
            fetchedCompanyIdRef.current = null;
            failedCompanyIdRef.current = null;
        }
    }, [user, company, dispatch]);

    useEffect(() => {
        if (effectiveCompanyId !== fetchedCompanyIdRef.current) {
            failedCompanyIdRef.current = null;
        }
    }, [effectiveCompanyId]);

    return {
        company,
        loading,
        error,
        refetchCompanyData,
        hasCompanyData: !!company,
        hasUser: !!user
    };
}; 